import { Info, TrendingUp, TrendingDown, Wallet } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import type { CampaignCalculationResult } from '../hooks/useCampaignCalculations';

interface CampaignFinancialSummaryProps {
  calculations: CampaignCalculationResult;
  clientBudget: number;
  influencersCount: number;
}

export function CampaignFinancialSummary({
  calculations,
  clientBudget,
  influencersCount,
}: CampaignFinancialSummaryProps) {
  const { t } = useLanguage();
  const {
    totalInfluencerCosts,
    totalServiceCosts,
    totalInfluencerCommissions,
    operationalMargin,
    isCommissionOnlyMode,
    marginIsPositive,
  } = calculations;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(amount);

  return (
    <div className="space-y-4">
      {isCommissionOnlyMode && influencersCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <Wallet className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-800">
            <strong>{t('financial.commissionOnlyMode')}:</strong> {t('financial.commissionOnlyDescription')}
          </div>
        </div>
      )}

      <div className="bg-accent-50 border border-accent-200 rounded-lg p-4 space-y-2">
        <h4 className="font-semibold text-gray-900 mb-2">{t('financial.summary')}</h4>

        <div className="space-y-1 text-sm">
          {!isCommissionOnlyMode && clientBudget > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-700">{t('financial.campaignBudget')}:</span>
              <span className="font-medium">{formatCurrency(clientBudget)}</span>
            </div>
          )}

          {isCommissionOnlyMode && (
            <div className="flex justify-between">
              <span className="text-gray-700">{t('financial.mode')}:</span>
              <span className="font-medium text-amber-700">{t('financial.commissionOnly')}</span>
            </div>
          )}

          <div className="flex justify-between">
            <span className="text-gray-700">{t('financial.influencerFees')}:</span>
            <span className="font-medium text-red-600">
              -{formatCurrency(totalInfluencerCosts)}
            </span>
          </div>

          {totalServiceCosts > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-700">{t('financial.serviceCosts')}:</span>
              <span className="font-medium text-red-600">
                -{formatCurrency(totalServiceCosts)}
              </span>
            </div>
          )}

          <div className="flex justify-between border-t border-accent-200 pt-1">
            <span className="text-gray-700">{t('financial.agencyCommissions')}:</span>
            <span className="font-medium text-accent-700">
              +{formatCurrency(totalInfluencerCommissions)}
            </span>
          </div>

          <div className="flex justify-between border-t-2 border-accent-400 pt-2 mt-2">
            <span className="font-bold text-gray-900 flex items-center gap-1">
              {isCommissionOnlyMode ? t('financial.totalRevenue') : t('financial.totalMargin')}:
              {marginIsPositive ? (
                <TrendingUp className="w-4 h-4 text-green-600" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-600" />
              )}
            </span>
            <span
              className={`font-bold text-lg ${
                marginIsPositive ? 'text-green-700' : 'text-red-700'
              }`}
            >
              {formatCurrency(operationalMargin)}
            </span>
          </div>
        </div>

        {!isCommissionOnlyMode && (
          <p className="text-xs text-gray-500 mt-2">
            {t('financial.formulaStandard')}
          </p>
        )}

        {isCommissionOnlyMode && (
          <p className="text-xs text-gray-500 mt-2">
            {t('financial.formulaCommissionOnly')}
          </p>
        )}
      </div>

      {!marginIsPositive && !isCommissionOnlyMode && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{t('financial.negativeMarginWarning')}</span>
        </div>
      )}
    </div>
  );
}
